import React, { useState, useRef, useEffect, useCallback } from "react";
import { sendAssistantMessage, resetSession } from "../../services/chatbot";
import "./chatbot.css";

const GREETING =
  "Hi! I'm the WayaQuick assistant. Ask me anything about payments, links, or getting started.";

const INITIAL_SUGGESTIONS = [
  "What is WayaQuick?",
  "How do I create a payment link?",
  "What are your transaction fees?",
  "How do I integrate with my website?",
];

// Persist the transcript so a page reload restores the conversation.
// (localStorage survives reload and return visits; swap to sessionStorage for
// per-tab-only if you prefer it cleared when the tab closes.)
const MSG_KEY = "waya_assistant_messages";
const SUG_KEY = "waya_assistant_suggestions";
const MAX_STORED = 50;

let seq = 0;
function nextId() {
  seq += 1;
  return String(Date.now()) + "-" + seq;
}

function loadMessages() {
  try {
    const raw = localStorage.getItem(MSG_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr) && arr.length) return arr;
    }
  } catch (e) {
    /* ignore */
  }
  return [{ id: nextId(), role: "bot", text: GREETING }];
}

function loadSuggestions() {
  try {
    const raw = localStorage.getItem(SUG_KEY);
    if (raw) {
      const arr = JSON.parse(raw);
      if (Array.isArray(arr)) return arr;
    }
  } catch (e) {
    /* ignore */
  }
  return INITIAL_SUGGESTIONS;
}

function WebsiteChatAssistant() {
  const [open, setOpen] = useState(false);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState(loadMessages);
  const [suggestions, setSuggestions] = useState(loadSuggestions);

  const endRef = useRef(null);
  const inputRef = useRef(null);

  // Restore-safe persistence: write on every change.
  useEffect(() => {
    try {
      localStorage.setItem(MSG_KEY, JSON.stringify(messages.slice(-MAX_STORED)));
    } catch (e) {
      /* ignore */
    }
  }, [messages]);

  useEffect(() => {
    try {
      localStorage.setItem(SUG_KEY, JSON.stringify(suggestions));
    } catch (e) {
      /* ignore */
    }
  }, [suggestions]);

  useEffect(() => {
    if (open && endRef.current) {
      endRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, sending, open]);

  useEffect(() => {
    if (open && inputRef.current) {
      const t = setTimeout(() => {
        if (inputRef.current) inputRef.current.focus();
      }, 150);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  const send = useCallback(
    async (raw) => {
      const text = (raw || "").trim();
      if (!text || sending) return;

      setMessages((prev) => prev.concat([{ id: nextId(), role: "user", text: text }]));
      setInput("");
      setSending(true);

      try {
        const result = await sendAssistantMessage(text);
        setMessages((prev) =>
          prev.concat([{ id: nextId(), role: "bot", text: result.answer }])
        );
        if (result.suggestions && result.suggestions.length) {
          setSuggestions(result.suggestions);
        }
      } catch (e) {
        setMessages((prev) =>
          prev.concat([
            {
              id: nextId(),
              role: "bot",
              text:
                "Sorry, I couldn't reach the assistant right now. Please try again in a moment.",
            },
          ])
        );
      } finally {
        setSending(false);
      }
    },
    [sending]
  );

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const startNewChat = () => {
    resetSession();
    try {
      localStorage.removeItem(MSG_KEY);
      localStorage.removeItem(SUG_KEY);
    } catch (e) {
      /* ignore */
    }
    setMessages([{ id: nextId(), role: "bot", text: GREETING }]);
    setSuggestions(INITIAL_SUGGESTIONS);
    setInput("");
  };

  return (
    <React.Fragment>
      {open && (
        <div className="wq-chat-panel" role="dialog" aria-label="WayaQuick assistant">
          <div className="wq-chat-header">
            <div className="wq-chat-title">
              <span className="wq-dot" /> WayaQuick Assistant
            </div>
            <div className="wq-chat-actions">
              <button
                className="wq-chat-icon-btn"
                onClick={startNewChat}
                aria-label="Start new chat"
                title="Start new chat"
                type="button"
              >
                <svg
                  viewBox="0 0 24 24"
                  width="18"
                  height="18"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path d="M21 12a9 9 0 1 1-2.64-6.36" />
                  <path d="M21 3v5h-5" />
                </svg>
              </button>
              <button
                className="wq-chat-icon-btn"
                onClick={() => setOpen(false)}
                aria-label="Close chat"
                title="Close"
                type="button"
              >
                <span className="wq-x">&times;</span>
              </button>
            </div>
          </div>

          <div className="wq-chat-body">
            {messages.map((m) => (
              <div
                key={m.id}
                className={"wq-row " + (m.role === "user" ? "wq-row-user" : "wq-row-bot")}
              >
                <div
                  className={
                    "wq-bubble " + (m.role === "user" ? "wq-bubble-user" : "wq-bubble-bot")
                  }
                >
                  {m.text}
                </div>
              </div>
            ))}

            {sending && (
              <div className="wq-row wq-row-bot">
                <div className="wq-bubble wq-bubble-bot" aria-label="Assistant is typing">
                  <span className="wq-typing">
                    <span />
                    <span />
                    <span />
                  </span>
                </div>
              </div>
            )}

            <div ref={endRef} />
          </div>

          {suggestions && suggestions.length > 0 && (
            <div className="wq-suggestions">
              {suggestions.map((q, i) => (
                <button
                  key={q + "-" + i}
                  className="wq-chip"
                  type="button"
                  onClick={() => send(q)}
                  disabled={sending}
                >
                  {q}
                </button>
              ))}
            </div>
          )}

          <form className="wq-input-bar" onSubmit={onSubmit}>
            <input
              ref={inputRef}
              className="wq-input"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Type your message..."
              aria-label="Message"
            />
            <button
              className="wq-send"
              type="submit"
              disabled={!input.trim() || sending}
              aria-label="Send"
            >
              <svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true">
                <path d="M3 11l18-8-8 18-2-7-8-3z" fill="currentColor" />
              </svg>
            </button>
          </form>
        </div>
      )}

      <button
        className="wq-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={open ? "Close assistant" : "Open assistant"}
        type="button"
      >
        {open ? (
          <span className="wq-launcher-x">&times;</span>
        ) : (
          <svg viewBox="0 0 24 24" width="26" height="26" aria-hidden="true">
            <path
              d="M4 4h16a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H8l-4 4V6a2 2 0 0 1 2-2z"
              fill="currentColor"
            />
          </svg>
        )}
      </button>
    </React.Fragment>
  );
}

export default WebsiteChatAssistant;
